"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Session } from '@supabase/supabase-js';
import { getNoteById, updateNote, NoteItem } from '@/lib/supabase';
import ImageUploader from '@/components/ImageUploader';
import RichTextEditor from '@/components/RichTextEditor';
import LocationInput, { NoteLocation } from '@/components/LocationInput';
import CategorySelector from '@/components/CategorySelector';
import MultiStepNoteForm from '@/components/MultiStepNoteForm';
import { FileText, Loader2 } from '@/components/icons';
import { Skeleton } from '@/components/ui/skeleton';
import { endOfDay } from 'date-fns';

interface EditNotePageProps {
  session: Session | null;
}

const EditNotePage: React.FC<EditNotePageProps> = ({ session }) => {
  const { noteId } = useParams<{ noteId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [note, setNote] = useState<NoteItem | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [location, setLocation] = useState<NoteLocation | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const currentUserId = session?.user?.id;

  const fetchNoteData = useCallback(async () => {
    if (!noteId) {
      toast({ title: "Error", description: "No se especificó una nota para editar.", variant: "destructive" });
      navigate('/notas');
      return;
    }
    setLoading(true);
    try {
      const fetchedNote = await getNoteById(noteId);
      if (!fetchedNote) {
        toast({ title: "Error", description: "La nota que intentas editar no existe.", variant: "destructive" });
        navigate('/notas');
        return;
      }

      const isAuthor = fetchedNote.user_id === currentUserId;
      const isEditableTime = new Date() < endOfDay(new Date(fetchedNote.created_at));

      if (!isAuthor || !isEditableTime) {
        toast({ title: "No permitido", description: "No tienes permiso para editar esta nota o el tiempo para editar ha expirado.", variant: "destructive" });
        navigate(`/notas/${noteId}`);
        return;
      }

      setNote(fetchedNote);
      setTitle(fetchedNote.title);
      setContent(fetchedNote.content);
      setPhotoUrl(fetchedNote.photo_url);
      setLocation(fetchedNote.location_display_name ? { displayName: fetchedNote.location_display_name, latitude: fetchedNote.latitude!, longitude: fetchedNote.longitude! } : null);
      setSelectedCategoryId(fetchedNote.category_id);
    } catch (error) {
      toast({ title: "Error", description: "No se pudo cargar la nota para editar.", variant: "destructive" });
      navigate('/notas');
    } finally {
      setLoading(false);
    }
  }, [noteId, currentUserId, navigate, toast]);

  useEffect(() => {
    fetchNoteData();
  }, [fetchNoteData]);

  const handleUpdate = async () => {
    if (!note || !currentUserId) return;

    setSaving(true);
    try {
      await updateNote(note.id, currentUserId, {
        title,
        content,
        photo_url: photoUrl,
        location_display_name: location?.displayName || null,
        latitude: location?.latitude || null,
        longitude: location?.longitude || null,
        category_id: selectedCategoryId,
      });
      toast({
        title: "Nota actualizada",
        description: "Tu nota ha sido guardada con los cambios.",
      });
      navigate(`/notas/${note.id}`);
    } catch (error) {
      toast({
        title: "Error al actualizar",
        description: "No se pudo guardar los cambios. Inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-2">
        <FileText className="h-6 w-6" /> Editando Nota
      </h1>

      <MultiStepNoteForm
        session={session}
        initialValues={{
          id: note?.id,
          title: note?.title,
          content: note?.content,
          photoUrl: note?.photo_url || null,
          // Include any array of image URLs returned by the backend (snake_case or camelCase)
          imageUrls: (note as any)?.image_urls ?? (note as any)?.imageUrls ?? null,
          location: note?.location_display_name ? { displayName: note.location_display_name, latitude: note.latitude ?? null, longitude: note.longitude ?? null } as NoteLocation : null,
          categoryId: note?.category_id || null,
        }}
        singleView={true}
        onCancel={() => navigate(`/notas/${noteId}`)}
        onSubmit={async (payload) => {
          if (!note || !currentUserId) throw new Error('Nota o usuario no disponible');
          setSaving(true);
          try {
            await updateNote(note.id, currentUserId, {
              title: payload.title,
              content: payload.content,
              photo_url: payload.photoUrl,
              image_urls: (payload as any).imageUrls ?? null,
              location_display_name: payload.locationDisplayName,
              latitude: payload.latitude,
              longitude: payload.longitude,
              category_id: payload.categoryId,
            });
          } finally {
            setSaving(false);
          }
        }}
        onSuccess={(id) => {
          navigate(`/notas/${note?.id}`);
        }}
      />
    </div>
  );
};

export default EditNotePage;